import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import VideoCard from "@/components/feature/VideoCard";
import PlayerModal from "@/pages/detail/components/PlayerModal";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { getCategoryGroup, getSubCategory } from "@/mocks/categories";
import {
  formatViews,
  getRelatedVideos,
  getVideoById,
  typeLabels,
} from "@/utils/video";

export default function DetailPage() {
  const { id = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const video = getVideoById(id);
  const [playerOpen, setPlayerOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("play") === "1") {
      setPlayerOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useDocumentMeta({
    title: video ? `${video.title} - 逼特逼视频详情` : "视频详情 - 逼特逼视频",
    description: video
      ? `${video.title}：${video.summary.slice(0, 80)}（${video.year}年${video.region}${typeLabels[video.type]}，导演${video.director}）`
      : "该视频可能已下架，浏览其他精彩内容。",
    keywords: video
      ? `${video.title},${video.genres.join(",")},${typeLabels[video.type]},在线观看,逼特逼视频`
      : "视频详情,逼特逼视频",
  });

  if (!video) {
    return (
      <div className="page-shell py-16 md:py-24">
        <EmptyState
          icon="ri-file-unknow-line"
          title="抱歉，该视频已下架或不存在"
          description="链接可能已经失效，回到首页看看今天新上线的作品吧。"
        />
        <div className="mt-6 flex justify-center gap-3">
          <Link
            to="/"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-400"
          >
            搜索其他视频
          </Link>
        </div>
      </div>
    );
  }

  const sub = getSubCategory(video.categoryId);
  const group = getCategoryGroup(video.type);
  const related = getRelatedVideos(video, 6);
  const metaRows = [
    { label: "类型", value: video.genres.join(" / ") },
    { label: "地区", value: video.region },
    { label: "上映年份", value: `${video.year} 年` },
    { label: video.type === "series" ? "集数" : "时长", value: video.duration },
    { label: "导演 / 制作", value: video.director },
    { label: "更新时间", value: video.updatedAt },
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden border-b border-background-200">
        <div className="absolute inset-0">
          <img
            src={video.cover}
            alt={`${video.title}背景海报`}
            className="h-full w-full object-cover object-top opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-background-50/60" />
        </div>

        <div className="page-shell relative py-7 md:py-12">
          <Breadcrumb
            items={[
              ...(group ? [{ label: `${group.name}频道`, to: `/category/${group.type}` }] : []),
              ...(sub ? [{ label: sub.name, to: `/list/${sub.id}` }] : []),
              { label: video.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr] md:gap-8 lg:grid-cols-[260px_1fr]">
            <div className="relative hidden overflow-hidden rounded-lg border border-background-300/60 md:block">
              <div className="aspect-[4/5] w-full">
                <img
                  src={video.cover}
                  alt={`${video.title}封面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <button
                type="button"
                onClick={() => setPlayerOpen(true)}
                className="absolute inset-0 flex items-center justify-center bg-background-50/30 opacity-0 transition-opacity hover:opacity-100"
                aria-label={`播放${video.title}`}
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-play-fill text-2xl" />
                </span>
              </button>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-primary-500 px-2 py-0.5 font-label text-[11px] text-background-50">
                  {typeLabels[video.type]}
                </span>
                <span className="rounded border border-background-300/70 bg-background-100/60 px-2 py-0.5 font-label text-[11px] text-foreground-700 backdrop-blur">
                  {video.year}
                </span>
                <span className="rounded border border-accent-400/40 bg-accent-500/10 px-2 py-0.5 font-label text-[11px] text-accent-300 backdrop-blur">
                  {video.region}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-2xl font-semibold leading-tight tracking-tight text-foreground-950 sm:text-3xl md:text-4xl">
                {video.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600 md:text-sm">
                <span className="flex items-center gap-1 text-primary-400">
                  <i className="ri-star-fill text-sm" />
                  <strong className="font-label font-semibold">{video.rating.toFixed(1)}</strong>
                  <span className="text-foreground-500">/ 10</span>
                </span>
                <span>{video.genres.join(" · ")}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-circle-line text-sm" />
                  {formatViews(video.views)}次播放
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-sm" />
                  {video.duration}
                </span>
              </div>

              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">
                {video.summary}
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => setPlayerOpen(true)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg" />
                  播放视频
                </button>
                <a
                  href="#related"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100/60 px-6 py-3 text-sm text-foreground-900 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-400"
                >
                  <i className="ri-links-line text-base" />
                  查看相关推荐
                </a>
                <span className="text-[11px] leading-relaxed text-foreground-500">
                  播放为演示效果
                  <br className="hidden sm:block" />
                  不接入真实片源
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="page-shell py-8 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px] lg:gap-8">
          <div className="min-w-0 space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-medium text-foreground-900">剧情简介</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">{video.summary}</p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                影片由{video.director}执导，{video.cast.join("、")}联合出演，
                {video.year}年上线于{video.region}。目前为{typeLabels[video.type]}频道
                {sub ? `「${sub.name}」` : ""}分类下的推荐内容，
                {video.type === "series" ? `已更新${video.duration}` : `片长${video.duration}`}。
              </p>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-medium text-foreground-900">影片信息</h2>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-3 border-b border-background-200/70 pb-3">
                    <dt className="w-20 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 text-sm text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4">
                <p className="text-xs text-foreground-500">主演</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {video.cast.map((name) => (
                    <span
                      key={name}
                      className="rounded border border-background-200 bg-background-50 px-2.5 py-1 text-xs text-foreground-700"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section id="related" className="scroll-mt-24">
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">
                    相关推荐
                  </h2>
                  <p className="mt-1 text-xs text-foreground-500">
                    同分类与同导演作品，按相关度排序
                  </p>
                </div>
                <Link
                  to={`/category/${video.type}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-400"
                >
                  更多{typeLabels[video.type]}
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>

              {related.length === 0 ? (
                <EmptyState title="暂无相关推荐" description="我们正在补充同类内容。" />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {related.map((item) => (
                    <VideoCard key={item.id} video={item} />
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-sm font-medium text-foreground-900">所属分类</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {group && (
                  <Link
                    to={`/category/${group.type}`}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500/15 px-2.5 py-1.5 text-xs text-primary-400 transition-colors hover:bg-primary-500/25"
                  >
                    <i className={`${group.icon} text-sm`} />
                    {group.name}频道
                  </Link>
                )}
                {sub && (
                  <Link
                    to={`/list/${sub.id}`}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400"
                  >
                    {sub.name}
                    <i className="ri-arrow-right-s-line text-sm" />
                  </Link>
                )}
                {video.genres.map((genre) => (
                  <span
                    key={genre}
                    className="whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-600"
                  >
                    #{genre}
                  </span>
                ))}
              </div>
              <Link
                to={`/list/${sub?.id ?? video.type}`}
                className="mt-4 inline-flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-primary-400"
              >
                <i className="ri-arrow-left-line text-sm" />
                返回{sub?.name ?? typeLabels[video.type]}列表
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-1.5 font-heading text-sm font-medium text-foreground-900">
                <i className="ri-flashlight-line text-primary-400" />
                同类型热门
              </h3>
              <ul className="mt-3 space-y-3">
                {getRelatedVideos(video, 4).map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-3"
                    >
                      <span className="w-4 shrink-0 font-label text-xs text-foreground-500">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <img
                        src={item.cover}
                        alt={`${item.title}缩略图`}
                        className="h-12 w-[74px] shrink-0 rounded object-cover object-top"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-foreground-800 transition-colors group-hover:text-primary-400">
                          {item.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-500">
                          {item.year} · {item.rating.toFixed(1)} 分
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-sm font-medium text-foreground-900">观看提示</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                当前站点为内容浏览演示版本，播放按钮会展开模拟播放器，并提示
                “演示模式，非真实播放”。无需登录，也无任何付费环节。
              </p>
            </div>
          </aside>
        </div>
      </div>

      {playerOpen && <PlayerModal video={video} onClose={() => setPlayerOpen(false)} />}
    </div>
  );
}